import {
  motion,
  useInView,
  useReducedMotion,
  useScroll,
  useTransform,
  type Variants,
} from 'framer-motion';
import { useRef, type ReactNode, useEffect, useState } from 'react';

/* --------------------------------------------------------------------------
 * MaskReveal — the "liquid fluid" cinematic reveal.
 *
 * The content is clipped via clip-path: inset() that animates from the
 * bottom (100% inset) up to 0% inset, producing a smooth upward wipe that
 * feels like a liquid being poured over the section. A subtle gold sheen
 * veil slides over the content right before it reveals, then fades out.
 * -------------------------------------------------------------------------- */
interface MaskRevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  /** Tilt angle of the wipe edge in degrees. Slight tilt feels more organic. */
  tilt?: number;
  /** Shows a subtle gold gradient sheen that wipes across the section. */
  sheen?: boolean;
  once?: boolean;
}

export function MaskReveal({
  children,
  className,
  delay = 0,
  duration = 1.1,
  tilt = -2,
  sheen = true,
  once = true,
}: MaskRevealProps) {
  const reduced = useReducedMotion();

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  const veilVariants: Variants = {
    hidden: { clipPath: 'inset(100% 0% 0% 0%)', opacity: 1 },
    visible: {
      clipPath: 'inset(0% 0% 0% 0%)',
      opacity: 0,
      transition: { duration, delay, ease: [0.83, 0, 0.17, 1] },
    },
  };

  const sheenVariants: Variants = {
    hidden: { x: '-120%', opacity: 0 },
    visible: {
      x: '120%',
      opacity: [0, 0.5, 0],
      transition: { duration: duration * 1.4, delay: delay + 0.1, ease: [0.65, 0, 0.35, 1] },
    },
  };

  const viewport = { once, amount: 0.15, margin: '-15% 0px -15% 0px' };

  return (
    <div className={cnWrap(className)}>
      <motion.div
        aria-hidden
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={veilVariants}
        style={{
          transform: `rotate(${tilt}deg) scale(1.06)`,
          transformOrigin: 'center bottom',
        }}
        className="pointer-events-none absolute inset-0 origin-bottom bg-bg"
      />
      {sheen && (
        <motion.div
          aria-hidden
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={sheenVariants}
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-1/3 bg-gradient-to-r from-transparent via-gold-500/15 to-transparent blur-2xl"
        />
      )}
      <div className="relative">{children}</div>
    </div>
  );
}

function cnWrap(c?: string) {
  return `relative overflow-hidden ${c ?? ''}`;
}

/* --------------------------------------------------------------------------
 * TextSplit — staggered word-by-word reveal that blurs in from below.
 * Best for cinematic hero headlines.
 * -------------------------------------------------------------------------- */
interface TextSplitProps {
  text: string;
  className?: string;
  wordClassName?: string;
  delay?: number;
  stagger?: number;
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span';
  /** Highlight trailing words (e.g., gold accent) by index. */
  accentFrom?: number;
  accentClassName?: string;
}

export function TextSplit({
  text,
  className,
  wordClassName,
  delay = 0,
  stagger = 0.06,
  as: Tag = 'h1',
  accentFrom,
  accentClassName = 'text-gold-500',
}: TextSplitProps) {
  const reduced = useReducedMotion();

  const MotionTag = motion[Tag] as typeof motion.h1;
  const words = text.split(' ');

  if (reduced) {
    return <Tag className={className}>{text}</Tag>;
  }

  return (
    <MotionTag
      className={className}
      aria-label={text}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2, margin: '-10% 0px -10% 0px' }}
      variants={{
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
        hidden: {},
      }}
    >
      {words.map((word, i) => {
        const isAccent = accentFrom !== undefined && i >= accentFrom;
        return (
          <span
            key={`${word}-${i}`}
            aria-hidden
            className="inline-block overflow-hidden align-baseline"
            style={{ paddingBottom: '0.1em', marginBottom: '-0.1em' }}
          >
            <motion.span
              variants={{
                hidden: { y: '110%', opacity: 0, filter: 'blur(8px)' },
                visible: {
                  y: '0%',
                  opacity: 1,
                  filter: 'blur(0px)',
                  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                },
              }}
              className={`inline-block ${wordClassName ?? ''} ${isAccent ? accentClassName : ''}`}
            >
              {word}
              {i < words.length - 1 ? '\u00A0' : ''}
            </motion.span>
          </span>
        );
      })}
    </MotionTag>
  );
}

/* --------------------------------------------------------------------------
 * Parallax — scroll-linked vertical translation.
 * Pass `speed` between -1 and 1. Negative = slower than scroll (background drift),
 * positive = faster than scroll (foreground float). Zero = normal scroll.
 * -------------------------------------------------------------------------- */
interface ParallaxProps {
  children: ReactNode;
  className?: string;
  speed?: number;
  /** Optional max horizontal drift in px (sin wave on scroll). */
  drift?: number;
}

export function Parallax({ children, className, speed = -0.2, drift = 0 }: ParallaxProps) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const yRange = 200 * speed;
  const y = useTransform(scrollYProgress, [0, 1], [Math.abs(yRange), -Math.abs(yRange)]);
  const x = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    drift === 0 ? [0, 0, 0] : [-drift, 0, drift],
  );

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div ref={ref} style={{ y, x }} className={className}>
      {children}
    </motion.div>
  );
}

/* --------------------------------------------------------------------------
 * SectionStage — composes parallax depth into one wrapper for hero sections
 * that have layered backgrounds. Optional fade as the section approaches viewport.
 * -------------------------------------------------------------------------- */
export function SectionStage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.85, 1], [0.6, 1, 1, 0.6]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.98, 1, 0.99]);

  if (reduced) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      ref={ref}
      style={{ opacity, scale }}
      transition={{ duration: 0.6 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* --------------------------------------------------------------------------
 * Counter — number that ticks up when it enters view.
 * -------------------------------------------------------------------------- */
export function CountUp({
  to,
  duration = 1.6,
  prefix = '',
  suffix = '',
  className,
}: {
  to: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduced = useReducedMotion();
  const [val, setVal] = useState(reduced ? to : 0);

  useEffect(() => {
    if (!inView || reduced) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(eased * to));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduced, to, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {val}
      {suffix}
    </span>
  );
}
